import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import InstallButton from "@/components/InstallButton";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import {
  LayoutDashboard, Users, GraduationCap, Receipt, Wallet, LogOut,
  Store, Package, ScrollText, Bot, Menu, X, Database, Upload, Coins,
  PiggyBank, FileBarChart, ChevronDown, History, QrCode, KeyRound, UserCog, BookUser, ClipboardList, CreditCard,
} from "lucide-react";

const NAV = {
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { group: "Data", icon: Database, children: [
      { to: "/admin/import", label: "Impor Data", icon: Upload },
      { to: "/admin/parents", label: "Orang Tua", icon: Users },
      { to: "/admin/students", label: "Santri", icon: GraduationCap },
      { to: "/admin/qr", label: "Kartu QR Santri", icon: QrCode },
      { to: "/admin/staff", label: "Akun Petugas", icon: UserCog },
    ]},
    { group: "Keuangan", icon: Wallet, children: [
      { to: "/admin/bills", label: "Tagihan", icon: Receipt },
      { to: "/admin/tabungan", label: "Tabungan", icon: PiggyBank },
      { to: "/admin/rekap-tabungan", label: "Rekap Tabungan", icon: FileBarChart },
      { to: "/admin/laporan", label: "Laporan Pembayaran", icon: FileBarChart },
      { to: "/admin/transaksi-online", label: "Transaksi Online", icon: Receipt },
    ]},
    { group: "Pendidik & Kependidikan", icon: BookUser, children: [
      { to: "/admin/pendidik", label: "Data Pendidik", icon: BookUser },
      { to: "/admin/pendidik/qr", label: "Kartu QR Pendidik", icon: QrCode },
      { to: "/admin/pendidik/laporan", label: "Laporan Belanja", icon: ClipboardList },
    ]},
    { group: "Kopontren", icon: Store, children: [
      { to: "/admin/kop/saldo", label: "Uang Jajan", icon: Wallet },
      { to: "/admin/kop/history-saldo", label: "History Uang Jajan", icon: History },
      { to: "/admin/kop/asc", label: "Kartu ASC", icon: CreditCard },
      { to: "/admin/kop/asc-laporan", label: "Laporan ASC", icon: FileBarChart },
      { to: "/admin/kop/stok", label: "Stok Barang", icon: Package },
      { to: "/admin/kop/market-day", label: "Mode Market Day", icon: Store },
      { to: "/admin/kop/laporan", label: "Laporan Penjualan", icon: ScrollText },
      { to: "/admin/kop/rekap-saldo-belanja", label: "Rekap Uang Jajan & Belanja", icon: FileBarChart },
    ]},
  ],
  parent: [
    { to: "/parent", label: "Beranda", icon: LayoutDashboard, end: true },
    { to: "/parent/tagihan", label: "Tagihan", icon: Receipt },
    { to: "/parent/riwayat", label: "Riwayat Pembayaran", icon: History },
    { to: "/parent/history-saldo", label: "History Uang Jajan & Tabungan", icon: PiggyBank },
    { to: "/parent/assistant", label: "Asisten AI", icon: Bot },
  ],
  cashier: [
    { to: "/pos", label: "Penjualan", icon: Store, end: true },
    { to: "/pos/products", label: "Stok Barang", icon: Package },
    { to: "/pos/sales", label: "Laporan Penjualan", icon: ScrollText },
  ],
};

const ROLE_LABEL = { admin: "Admin Pesantren", parent: "Orang Tua Santri", cashier: "Kasir Kopontren" };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const items = NAV[user?.role] || [];

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-stone-50">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-stone-900 text-stone-300 flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-6 flex items-center gap-3 border-b border-white/10">
          <img src={LOGO_URL} alt="Al-Khoir" className="w-12 h-12 object-contain" />
          <div>
            <p className="font-heading font-bold text-white leading-tight">Al-Khoir</p>
            <p className="text-[11px] text-emerald-400 uppercase tracking-widest">{ROLE_LABEL[user?.role]}</p>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {items.map((it, i) =>
            it.children ? (
              <NavGroup key={i} item={it} onNavigate={() => setOpen(false)} />
            ) : (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? "bg-emerald-600 text-white shadow-lg shadow-emerald-900/30"
                      : "hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <it.icon size={18} />
                {it.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="p-4 border-t border-white/10">
          <InstallButton />
          <button
            onClick={() => setPwOpen(true)}
            data-testid="change-password-button"
            className="flex w-full items-center gap-3 px-4 py-3 mb-1 rounded-xl text-sm font-medium text-stone-300 hover:bg-white/5 hover:text-white transition-all"
          >
            <KeyRound size={18} /> Ganti Password
          </button>
          <div className="px-3 py-2 mb-2">
            <p className="text-sm text-white font-medium truncate">{user?.name}</p>
            <p className="text-xs text-stone-400 font-mono">{user?.phone}</p>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="flex w-full items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-rose-300 hover:bg-rose-500/10 transition-all"
          >
            <LogOut size={18} /> Keluar
          </button>
        </div>
      </aside>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-stone-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(!open)} data-testid="menu-toggle" className="p-2">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <img src={LOGO_URL} alt="" className="w-9 h-9 object-contain" />
        </header>
        <main className="flex-1 p-5 md:p-8 lg:p-10 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}


function NavGroup({ item, onNavigate }) {
  const location = useLocation();
  const childActive = item.children.some((c) => location.pathname === c.to);
  const [open, setOpen] = useState(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`navgroup-${item.group.toLowerCase()}`}
        className="flex w-full items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-stone-300 hover:bg-white/5 hover:text-white transition-all"
      >
        <item.icon size={18} />
        <span className="flex-1 text-left">{item.group}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              onClick={onNavigate}
              data-testid={`nav-${c.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive ? "bg-emerald-600 text-white" : "text-stone-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <c.icon size={16} />
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
